import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import { RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid, ComparisonTable, RichFAQ, RelatedLinks, RichCTA, ChargerCard } from '../RichBlocks';
import RichEvBenefits from '../RichEvBenefits';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';
import { useChargerCatalog } from '@/components/charger/useChargerCatalog';
import V2Sections from '../V2Sections';

/**
 * EvModelRich — bespoke template for ev_model pages.
 * Builds a real per-model home-charging cost estimate from ev_data + a clean
 * spec/charge-time table. No competitor pricing.
 */
const ORDER_SKUS = ['nexblue_edge_2', 'zaptec_go', 'zaptec_go_2'];

export default function EvModelRich({ page }) {
  const { chargers } = useChargerCatalog();
  const cards = ORDER_SKUS
    .map((sku) => chargers.find((c) => c.sku === sku))
    .filter(Boolean)
    .map((c) => ({
      slug: c.slug,
      name: c.name,
      brand: c.brand,
      kw: 22,
      price: c.price || 0,
      originalPrice: c.original_price || c.price || 0,
      image: c.image,
    }));

  const ev = page?.ev_data || page?.pricing_data?.model || {};
  const brand = ev.brand || '';
  const model = ev.model || page?.competitor_name || 'din elbil';
  const carName = `${brand} ${model}`.trim();
  const consumption100 = ev.consumption_kwh_per_100km || ev.consumption || 16;
  const battery = ev.battery_kwh || ev.battery || null;
  const range = ev.range || (battery ? Math.round((battery / consumption100) * 100) : null);

  const geo = page?.geography || {};
  const place = geo.name ? ` i ${geo.name}` : '';
  const zone = geo.zone;
  const year = page?.year || 2026;
  const evData = page?.pricing_data?.evData || {};
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  // Home charging cost estimate: 40.000 km/år, 90% home = kWh; spotpris ~0.70 kr/kWh all-in (incl. afgift/tarif/moms, before laderabat)
  const kmYear = 15000;
  const homePct = 0.9;
  const kwhYear = Math.round((kmYear * consumption100 / 100) * homePct);
  const allInPerKwh = 1.6; // typical all-in incl. spotpris, elafgift ( before refund), tarif, moms — illustrative
  const chargeTime22 = battery ? Math.max(1, Math.round((battery / 22) * 10) / 10) : null;

  const fmt = (n) => Math.round(n).toLocaleString('da-DK');

  const faqs = (page?.faqs?.length ? page.faqs : [
    { question: `Hvad koster det at lade ${carName} hjemme?`, answer: `Med ${consumption100} kWh/100 km bruger ${carName} ca. ${fmt(kwhYear)} kWh/år ved hjemmeladning (${fmt(kmYear)} km, 90% hjemme). Hos enkel betaler du spotpris + 0 øre tillæg + 18 kr./md., og du får 10 øre/kWh laderabat oveni.` },
    { question: `Hvilken ladeboks passer til ${carName}?`, answer: `${carName} bruger Type 2-stik (europæisk standard) og passer til alle vores ladebokse: NexBlue Edge 2, Zaptec Go og Zaptec Go 2 – alle inkl. Saving Mode, serviceaftale og laderabat.` },
    { question: `Virker Saving Mode med ${carName}?`, answer: `Ja. Saving Mode virker med alle elbiler med Type 2-stik, inkl. ${carName}. Angiv din afgangstid i appen, så lader bilen automatisk i de billigste timer.` },
  ]);

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'specs', label: 'Specifikationer' },
    { id: 'ladeboks', label: 'Ladeboks' },
    { id: 'elbil', label: 'Besparelse' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Elbil-opladning', path: '/elbil' }, { name: carName, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={page?.h1} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Elbil-opladning${zone ? ' · ' + zone : ''} · ${year}`}
        h1={page?.h1 || `${carName} opladning${place} ${year}`}
        intro={page?.intro_text || `Alt om opladning af ${carName}: forbrug, ladeboks, ladetid og hvad det koster at lade hjemme med enkel energi.`}
        badges={['Spotpris + 0 øre', '10 øre/kWh laderabat', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${consumption100} kWh`, label: 'forbrug / 100 km' },
          ...(battery ? [{ value: `${battery} kWh`, label: 'batteri' }] : []),
          ...(range ? [{ value: `~${range} km`, label: 'rækkevidde (WLTP)' }] : []),
          { value: `~${fmt(kwhYear)} kWh`, label: 'hjemmeladning/år' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading={`Opladning af ${carName} – kort svar`}>
        <AnswerBox question={`Hvad koster det at lade ${carName} hjemme?`}>
          {carName} bruger ca. <strong>{consumption100} kWh/100 km</strong>. Ved {fmt(kmYear)} km/år med 90% hjemmeladning er det ca. <strong>{fmt(kwhYear)} kWh/år</strong>. Med enkel energi lader du til <strong>spotpris + 0 øre tillæg</strong>, får <strong>10 øre/kWh laderabat</strong>, og Saving Mode planlægger automatisk opladningen til de billigste timer.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="specs" heading={`${carName} – nøgletal`} alt>
        <FactGrid columns={3} facts={[
          { value: `${consumption100} kWh`, label: 'Forbrug pr. 100 km' },
          ...(battery ? [{ value: `${battery} kWh`, label: 'Batterikapacitet' }] : []),
          ...(range ? [{ value: `~${range} km`, label: 'Rækkevidde (WLTP)' }] : []),
          ...(chargeTime22 ? [{ value: `~${chargeTime22} t`, label: 'Ladetid 0-100% (22 kW)' }] : []),
          { value: 'Type 2', label: 'Ladestik' },
          { value: `~${fmt(kwhYear)} kWh`, label: 'Hjemmeladning pr. år' },
        ]} />
      </ProseSection>

      <V2Sections page={page} />

      <ProseSection id="ladeboks" heading={`Bedste ladeboks til ${carName}`}>
        <p>{carName} bruger Type 2-stik og passer til alle vores ladebokse. Alle inkluderer Saving Mode, serviceaftale og 10 øre/kWh laderabat:</p>
        {cards.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            {cards.map((c) => <ChargerCard key={c.slug} charger={c} />)}
          </div>
        )}
      </ProseSection>

      <RichEvBenefits evData={evData} heading={`Spar på opladning af ${carName}`} />

      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål om {carName}</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elbil', label: 'Elbil-opladning hos enkel' },
        { path: '/opladere', label: 'Se alle ladebokse' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh via Saving Mode' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/virker-enkel-med-alle-ladebokse', label: 'Er min ladeboks kompatibel?' },
      ]} />

      <RichCTA
        headline={`Lad ${carName} billigst med enkel`}
        subtext="Spotpris uden tillæg, 10 øre/kWh laderabat og Saving Mode inkluderet. Ladeboks fra 3.499 kr."
        primary={{ path: '/opladere', text: 'Se ladebokse' }}
        secondary={{ path: '/elaftale', text: 'Se el-aftaler' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}